import type { AgentActivityListener } from '@reflex/ui/components/agent/AgentActivityBar';
import type { AgentListener } from '@reflex/shared';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function remaining(ms: number): string {
  if (ms <= 0) return '0m';
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const minutes = Math.floor((ms % HOUR) / MINUTE);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function stateOf(listener: AgentListener): AgentActivityListener['state'] {
  switch (listener.status) {
    case 'pending_approval':
      return 'pending';
    case 'armed':
      return 'armed';
    case 'fired':
      return 'fired';
    case 'expired':
      return 'expired';
    case 'cancelled':
      return 'cancelled';
    case 'denied':
      return 'denied';
  }
}

/**
 * The dock row for a server listener. The label names what is listened to
 * (the ref handle and signal, the timer's delay, the watch's stream); the
 * secondary line carries the lease left and the agent's own reason, so a
 * person can tell at a glance what the agent is waiting on and why.
 */
export function listenerRow(
  listener: AgentListener & { ref?: string | null },
  now = Date.now(),
): AgentActivityListener {
  let label: string;
  switch (listener.kind) {
    case 'timer':
      label =
        listener.fireAt !== null && listener.status === 'armed'
          ? `in ${remaining(listener.fireAt - now)}`
          : 'timer';
      break;
    case 'signal':
      label = `${listener.ref ?? 'ref'} · ${listener.signal ?? ''}`;
      break;
    case 'watch':
      label =
        listener.breadth === 'scope' && listener.breadthScope
          ? `every ${listener.event ?? 'event'} in ${listener.breadthScope}`
          : listener.breadth === 'org'
            ? `every ${listener.event ?? 'event'} in the organization`
            : `${listener.source ?? ''} ${listener.event ?? ''}`;
      break;
  }
  const lease =
    listener.kind !== 'timer' && listener.expiresAt !== null && listener.status === 'armed'
      ? `${remaining(listener.expiresAt - now)} left`
      : '';
  return {
    id: listener.id,
    label,
    secondary: [lease, listener.reason].filter(Boolean).join(' · '),
    state: stateOf(listener),
    interrupts: listener.delivery === 'interrupt',
    fireCount: listener.maxFires > 1 ? listener.fireCount : 0,
    canRenew: listener.kind !== 'timer',
    canCancel: true,
  };
}
